<script setup lang="ts">
    import { computed, reactive, ref, watch } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import Modal from '@shared/components/Modal.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { billingAddressText, recipientIsValid } from './SmsTopUp.helpers';
    import ChargebeeCardPayment from './ChargebeeCardPayment.vue';
    import SmsTopUpTotal from './SmsTopUpTotal.vue';
    import SmsTopUpAddressStep from './SmsTopUpAddressStep.vue';
    import SmsTopUpInvoiceRecipient from './SmsTopUpInvoiceRecipient.vue';
    import SmsTopUpInvoiceCredit from './SmsTopUpInvoiceCredit.vue';
    import {
        ApiError,
        BillingAddress,
        PrePaidBalance,
        SmsTopUpOffer,
        SmsTopUpRefusal,
        SmsTopUpRequest,
        SmsTopUpInvoiceRecipient as InvoiceRecipient,
        TopUpTarget,
        addSmsTopUpToInvoice,
        estimateSmsTopUp,
        getPrePaidBalance,
        refusalOf,
    } from './AccountPrePaidBalance.access';

    const props = defineProps<{ offer: SmsTopUpOffer; amount: number; target: TopUpTarget; balance: PrePaidBalance; cameFrom: string | null }>();
    const emit = defineEmits<{ (e: 'funds-added', balance: PrePaidBalance): void; (e: 'busy', busy: boolean): void }>();

    // The one error shown: a refusal from the server, or how the call failed. start() and opening the invoice modal clear it.
    type TopUpError = SmsTopUpRefusal | 'Technical' | 'TryLater' | 'Reload';

    const state = reactive<{
        busy: boolean;
        paid: boolean;
        error: TopUpError | null;
        slowFunds: boolean;
        // The card's confirm failed with an unknown outcome: the slow-funds message says the payment may not have gone through.
        unconfirmed: boolean;
        fundsAdded: boolean;
        invoiced: boolean;
        viaCard: boolean;
        showInvoice: boolean;
        showCard: boolean;
        // The card step's confirm is in flight: the card modal cannot be closed.
        confirming: boolean;
        // The card modal's address, once its address step is done.
        cardAddress: BillingAddress | null;
        // The invoice modal: the address step first, then the total incl. VAT (D10).
        invoiceAddress: BillingAddress | null;
        invoiceTotal: { net: number; total: number } | null;
    }>({
        busy: false,
        paid: false,
        error: null,
        slowFunds: false,
        unconfirmed: false,
        fundsAdded: false,
        invoiced: false,
        viaCard: false,
        showInvoice: false,
        showCard: false,
        confirming: false,
        cardAddress: null,
        invoiceAddress: null,
        invoiceTotal: null,
    });

    watch(
        () => state.busy,
        (busy) => emit('busy', busy),
    );

    // Back to the text message the top-up came from (legacy PrePaid page: /TextMessages/<id>?mode=send).
    const backToSmsUrl = computed(() =>
        props.cameFrom && props.cameFrom !== 'ACCOUNT' && /^[A-Za-z0-9-]+$/.test(props.cameFrom) ? `/TextMessages/${props.cameFrom}?mode=send` : null,
    );

    // Idempotency key: the same for a double click / immediate retry of one request, new when the amount or target
    // changes (the same key with other parameters would replay the first invoice), after a 502 (Chargebee refused
    // definitively: nothing was created) and after a completed top-up. It is kept after a 503, 500, a network error
    // or a 409, so a retry replays the same invoice instead of risking a second one.
    let requestId = crypto.randomUUID();

    function renewRequestId(): void {
        requestId = crypto.randomUUID();
    }

    // The customer's own reference for the invoice (invoice path only; the card ignores it).
    const reference = ref('');
    const MAX_REFERENCE_LENGTH = 100;

    // Own billing details for the invoice (invoice path only; SMS top-up on invoice, R15). Null: the customer's address.
    const recipient = ref<InvoiceRecipient | null>(null);

    // The reference and the own details are part of the invoice, so a changed one is a new request (same key + other parameters
    // would replay the first invoice).
    watch(() => [props.amount, props.target, reference.value, JSON.stringify(recipient.value)], renewRequestId);

    function request(): SmsTopUpRequest {
        return { Amount: props.amount, Target: props.target, RequestId: requestId };
    }

    function invoiceRequest(): SmsTopUpRequest {
        const trimmed = reference.value.trim();
        return { ...request(), Reference: trimmed === '' ? null : trimmed, Recipient: recipient.value };
    }

    const currency = computed(() => props.offer.CurrencyCode ?? props.balance.CurrencyCode);

    // Nothing to offer: a refusal known up front, or a currency without a maximum (it cannot be topped up).
    const offerRefusal = computed<SmsTopUpRefusal | null>(() => (props.offer.Refusal !== 'None' ? props.offer.Refusal : null));
    const currencyNotSupported = computed(() => props.offer.Refusal === 'None' && props.offer.Maximum == null);

    const hasTooManyDecimals = computed(() => Math.abs(Math.round(props.amount * 100) - props.amount * 100) > 1e-6);
    const belowMinimum = computed(() => props.amount > 0 && props.amount < (props.offer.Minimum ?? 0));
    const aboveMaximum = computed(() => props.offer.Maximum != null && props.amount > props.offer.Maximum);

    // Client-side mirror of the server's checks; the server stays authoritative.
    const rangeRefusal = computed<SmsTopUpRefusal | null>(() => {
        if (props.amount > 0 && hasTooManyDecimals.value) return 'InvalidRequest';
        if (belowMinimum.value) return 'BelowMinimum';
        if (aboveMaximum.value) return 'AboveMaximum';
        return null;
    });

    const canSubmit = computed(() => props.amount > 0 && !rangeRefusal.value && !state.busy);

    // Mirror of the server: at most 100 characters, no control characters (blank means none).
    const referenceInvalid = computed(() => {
        const trimmed = reference.value.trim();
        return trimmed.length > MAX_REFERENCE_LENGTH || /[\u0000-\u001F\u007F-\u009F]/.test(trimmed);
    });
    // FormInput does not forward aria attributes, so they are set on its input element directly.
    const referenceInput = ref<HTMLInputElement | null>(null);
    watch([referenceInput, referenceInvalid], ([el, invalid]) => {
        if (!el) return;
        el.setAttribute('aria-invalid', String(invalid));
        if (invalid) el.setAttribute('aria-describedby', 'sms-top-up-reference-message');
        else el.removeAttribute('aria-describedby');
    });
    const canInvoice = computed(() => canSubmit.value && !referenceInvalid.value && recipientIsValid(recipient.value));

    function refusalMessage(refusal: SmsTopUpRefusal): string {
        switch (refusal) {
            case 'BelowMinimum':
                return format($Resources.SmsTopUpMinimumMessage, props.offer.Minimum, currency.value);
            case 'AboveMaximum':
                return format($Resources.SmsTopUpAboveMaximumMessage, props.offer.Maximum, currency.value);
            case 'InvalidRequest':
                return $Resources.SmsTopUpInvalidRequestMessage;
            case 'SubscriptionNotInGoodStanding':
                return $Resources.SmsTopUpSubscriptionNotInGoodStandingMessage;
            case 'NotTrustedForInvoice':
                return $Resources.SmsTopUpNotTrustedForInvoiceMessage;
            case 'AccountInactive':
                return $Resources.CreditCardActivationRequiredBody;
            case 'PaymentNotAuthorized':
                return $Resources.SmsTopUpCardDeclinedMessage;
            case 'NotPaymentAuthorized':
                return $Resources.SmsTopUpNotPaymentAuthorizedMessage ?? '';
            case 'AboveInvoiceLimit':
                return $Resources.SmsTopUpAboveInvoiceLimitMessage ?? '';
            case 'OverdueInvoice':
                return $Resources.SmsTopUpOverdueInvoiceMessage ?? '';
            case 'InvoiceDetailsMissing':
                return $Resources.SmsTopUpInvoiceDetailsMissingMessage ?? '';
            case 'InvoiceBusy':
                return $Resources.SmsTopUpInvoiceBusyMessage ?? '';
            // Someone who may not complete the address is told to ask an administrator (card path only, D2).
            case 'BillingAddressIncomplete':
                return props.offer.CanCompleteAddress ? $Resources.SmsTopUpReloadPageMessage : ($Resources.SmsTopUpAskAdministratorForAddressMessage ?? '');
            // The page's view of the address is stale (it was completed or changed elsewhere meanwhile).
            case 'BillingAddressLocked':
                return $Resources.SmsTopUpReloadPageMessage;
            default:
                return $Resources.SmsTopUpTechnicalErrorMessage;
        }
    }

    // template ?? '': a key not yet imported into SimpleLocalize is undefined at runtime.
    function format(template: string | undefined, value: number | null, currencyCode: string | null): string {
        return (template ?? '').replace('{0}', formatNumericText(value, { precision: Number.isInteger(value) ? 0 : 2, numberWithSpace: true }) ?? '').replace('{1}', currencyCode ?? '');
    }

    const invoiceSummary = computed(() =>
        ($Resources.SmsTopUpInvoiceSummary ?? '').replace('{0}', formatNumericText(props.amount, { precision: Number.isInteger(props.amount) ? 0 : 2, numberWithSpace: true }) ?? '')
            .replace('{1}', currency.value ?? '')
            .replace('{2}', props.target === 'SUBSCRIPTION' ? $Resources.SmsTopUpInvoiceTargetAll : $Resources.SmsTopUpInvoiceTargetAccount),
    );

    const shownMessage = computed(() => {
        if (currencyNotSupported.value) return $Resources.SmsTopUpCurrencyNotSupportedMessage;
        return offerRefusal.value ? refusalMessage(offerRefusal.value) : null;
    });

    const errorAlert = computed<{ type: AlertTypes; content: string } | null>(() => {
        switch (state.error) {
            case null:
                return null;
            case 'Technical':
                return { type: AlertTypes.Danger, content: $Resources.SmsTopUpTechnicalErrorMessage };
            case 'TryLater':
                return { type: AlertTypes.Warning, content: $Resources.SmsTopUpTryAgainLaterMessage };
            case 'Reload':
                return { type: AlertTypes.Warning, content: $Resources.SmsTopUpReloadPageMessage };
            default:
                return { type: AlertTypes.Warning, content: refusalMessage(state.error) };
        }
    });

    function handleError(error: ApiError | undefined): void {
        showError(error);
        state.busy = false;
    }

    // An address step's failure: shown inside its open modal, the form kept as filled in. busy is left as it is — the card modal
    // stays busy, so the amount and target cannot change under its open step.
    function showError(error: ApiError | undefined): void {
        if (error?.status === 409) {
            const refusal = refusalOf(error);
            // None / NotChargebeeOwned here means the page's view of the account is stale.
            state.error = !refusal || refusal === 'None' || refusal === 'NotChargebeeOwned' ? 'Reload' : refusal;
        } else if (error?.status === 503) {
            state.error = 'TryLater';
        } else {
            state.error = 'Technical';
            if (error?.status === 502) renewRequestId();
        }
    }

    // The card modal: the address step (SmsTopUpAddressStep), then the own card step (ChargebeeCardPayment). A retry — a declined
    // card, a failed 3DS challenge — renews the request id, so a new intent, and remounts the card step; the address is kept.
    const cardKey = ref(0);

    function payByCard(): void {
        start(true);
        state.cardAddress = null;
        state.showCard = true;
    }

    function cardAddressReady(address: BillingAddress): void {
        state.error = null;
        state.cardAddress = address;
    }

    function retryCard(): void {
        renewRequestId();
        cardKey.value++;
    }

    // Closing mid-payment charges nothing: an authorized but unconfirmed intent is released when it expires (Validation §36.5).
    // A new request id, so reopening starts a new intent rather than replaying the abandoned one. Not while confirming or
    // after: a closed step drops the confirm's answer (an unmounted component's emits are ignored), so a payment that went
    // through would never be waited for.
    function closeCard(): void {
        if (state.confirming || state.paid) return;
        renewRequestId();
        state.showCard = false;
        state.busy = false;
    }

    // The modal stays open, showing the balance being updated, until the funds land or the wait gives up (waitForFunds).
    async function cardPaid(): Promise<void> {
        state.confirming = false;
        state.paid = true;
        await waitForFunds();
    }

    // A new request id whatever failed: a retry with the old one would replay an intent that may already be authorized or
    // consumed (Validation §37.1, S3) — a fresh intent costs nothing, the old one expires.
    function cardFailed(error: ApiError | undefined): void {
        renewRequestId();
        state.confirming = false;
        state.showCard = false;
        handleError(error);
    }

    // The confirm may have failed after the paid invoice was created (Validation §37.1, S3): wait for the funds as after a payment
    // — the webhook adds them — rather than say it failed and have the customer pay again.
    async function cardUnconfirmed(): Promise<void> {
        state.unconfirmed = true;
        state.confirming = false;
        state.paid = true;
        await waitForFunds();
    }

    // A top-up completed since the modal was last opened: the next opening starts with an empty reference.
    let topUpCompleted = false;
    // Bumped after a completed invoice top-up, so the credit shown is read again.
    const creditKey = ref(0);

    function openInvoiceModal(): void {
        if (topUpCompleted) {
            reference.value = '';
            recipient.value = null;
            topUpCompleted = false;
        }
        state.error = null;
        state.invoiceAddress = null;
        state.invoiceTotal = null;
        state.showInvoice = true;
    }

    async function invoiceAddressReady(address: BillingAddress): Promise<void> {
        state.error = null;
        state.invoiceAddress = address;
        try {
            const estimate = await estimateSmsTopUp(request());
            state.invoiceTotal = { net: estimate.NetInMinorUnits, total: estimate.TotalInMinorUnits };
        } catch (error) {
            // The message is shown inside the open modal.
            handleError(error as ApiError);
        }
    }

    const invoiceAddressText = computed(() => billingAddressText(state.invoiceAddress));

    function closeInvoiceModal(): void {
        if (!state.busy) state.showInvoice = false;
    }

    async function addToInvoice(): Promise<void> {
        // A double click is a no-op while the first request is in flight (start() sets busy synchronously).
        if (state.busy) return;
        start(false);
        try {
            await addSmsTopUpToInvoice(invoiceRequest());
        } catch (error) {
            // The message is shown inside the open modal.
            handleError(error as ApiError);
            return;
        }
        // The modal stays open, showing the balance being updated, until waitForFunds closes it.
        state.invoiced = true;
        await waitForFunds();
    }

    // The balance before the purchase. Taken here, not after it: the Worker can add the funds before the card confirm or invoice
    // call answers, and a baseline read after that would already include them. Fresh, not the parent's: that one is stale
    // after an earlier slow-funds timeout.
    let baseline: Promise<PrePaidBalance | null> = Promise.resolve(null);

    function start(viaCard: boolean): void {
        baseline = getPrePaidBalance().catch(() => null);
        state.busy = true;
        state.viaCard = viaCard;
        state.confirming = false;
        state.error = null;
        state.slowFunds = false;
        state.unconfirmed = false;
        state.fundsAdded = false;
        state.invoiced = false;
    }

    // The funds usually land within seconds; after 30 s say so and let the webhook finish it. The open modal shows the wait and
    // is closed here, in the same tick as busy and paid are reset: closed later, the card step would remount for a moment.
    async function waitForFunds(): Promise<void> {
        renewRequestId();
        topUpCompleted = true;
        creditKey.value++;
        const before = ((await baseline) ?? props.balance).FullBalance ?? 0;
        for (let attempt = 0; attempt < 15; attempt++) {
            const balance = await getPrePaidBalance().catch(() => null);
            if (balance && (balance.FullBalance ?? 0) > before) {
                finishWaiting();
                state.fundsAdded = state.viaCard;
                emit('funds-added', balance);
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        finishWaiting();
        state.slowFunds = true;
    }

    function finishWaiting(): void {
        state.busy = false;
        state.paid = false;
        state.showCard = false;
        state.showInvoice = false;
    }
</script>

<template>
    <!-- An error while a modal is open shows inside it. -->
    <BsAlert v-if="errorAlert && !state.showInvoice && !state.showCard" :type="errorAlert.type" :dismissible="false" :content="errorAlert.content" />
    <BsAlert v-if="state.fundsAdded" :type="AlertTypes.Success" :dismissible="false" :content="$Resources.SmsTopUpFundsAddedMessage" />
    <BsAlert
        v-if="state.slowFunds"
        :type="state.unconfirmed ? AlertTypes.Warning : AlertTypes.Success"
        :dismissible="false"
        :content="
            state.unconfirmed
                ? $Resources.SmsTopUpPaymentUnconfirmedMessage
                : state.viaCard
                  ? $Resources.SmsTopUpReceivedMessage
                  : $Resources.SmsTopUpBalanceUpdatingMessage
        " />
    <BsAlert v-if="state.invoiced && !state.busy" :type="AlertTypes.Info" :dismissible="false" :content="$Resources.SmsTopUpInvoicedMessage" />

    <p v-if="backToSmsUrl && (state.invoiced || state.fundsAdded || state.slowFunds)">
        <a :href="backToSmsUrl" class="btn btn-cta">{{ $Resources.BackToTextMessageBtn }}</a>
    </p>

    <BsAlert v-if="currencyNotSupported || offerRefusal" :type="AlertTypes.Info" :dismissible="false" :content="shownMessage ?? ''" />

    <!-- Neither an administrator nor "Betalningsbehörig" (SMS top-up on invoice, R1–R3): nothing to buy with. -->
    <BsAlert
        v-else-if="!props.offer.CanPay"
        :type="AlertTypes.Info"
        :dismissible="false"
        :content="$Resources.SmsTopUpNotPaymentAuthorizedMessage ?? ''" />

    <template v-else>
        <p class="c-light" role="status">{{ rangeRefusal ? refusalMessage(rangeRefusal) : '' }}</p>
        <p class="c-light">{{ $Resources.SmsTopUpVatNote }}</p>

        <button class="btn btn-primary m-sm-r" :disabled="!canSubmit" @click="payByCard">
            {{ $Resources.SmsTopUpPayByCardBtn }}
        </button>
        <button class="btn btn-default" v-if="props.offer.CanInvoice" :disabled="!canSubmit" @click="openInvoiceModal">
            {{ $Resources.SmsTopUpAddToInvoiceBtn }}
        </button>
        <!-- The company's credit for "Lägg på faktura", for administrators; read again after each completed top-up. -->
        <SmsTopUpInvoiceCredit v-if="props.offer.CanInvoice && props.offer.CanCompleteAddress" :key="creditKey" />

        <Modal size="sm" :title="$Resources.SmsTopUpCardModalTitle" :show="state.showCard" :hideFooter="true" @close="closeCard">
            <template #content>
                <BsAlert v-if="errorAlert" :type="errorAlert.type" :dismissible="false" :content="errorAlert.content" />

                <Loading v-if="state.paid" :show="true" :labelSrOnly="false" :label="$Resources.SmsTopUpUpdatingBalanceLabel"></Loading>
                <template v-else-if="state.showCard">
                    <SmsTopUpAddressStep v-if="!state.cardAddress" :canComplete="props.offer.CanCompleteAddress" @ready="cardAddressReady" @failed="showError" />
                    <ChargebeeCardPayment
                        v-else
                        :key="cardKey"
                        :offer="props.offer"
                        :request="request()"
                        :address="state.cardAddress"
                        @confirming="state.confirming = true"
                        @paid="cardPaid"
                        @failed="cardFailed"
                        @unconfirmed="cardUnconfirmed"
                        @retry="retryCard" />
                </template>
            </template>
        </Modal>

        <Modal v-if="props.offer.CanInvoice" size="sm" :title="$Resources.SmsTopUpInvoiceModalTitle" :show="state.showInvoice" :hideFooter="true" @close="closeInvoiceModal">
            <template #content>
                <BsAlert v-if="errorAlert" :type="errorAlert.type" :dismissible="false" :content="errorAlert.content" />

                <Loading v-if="state.invoiced && state.busy" :show="true" :labelSrOnly="false" :label="$Resources.SmsTopUpUpdatingBalanceLabel"></Loading>

                <!-- The invoice needs the customer's complete billing address in Chargebee; nobody completes it here (R17): support does. -->
                <SmsTopUpAddressStep
                    v-else-if="!state.invoiceAddress"
                    :canComplete="false"
                    :incompleteMessage="$Resources.SmsTopUpInvoiceDetailsMissingMessage ?? ''"
                    @ready="invoiceAddressReady"
                    @failed="showError" />

                <template v-else>
                    <p>{{ invoiceSummary }}</p>
                    <p class="c-light">{{ $Resources.SmsTopUpBillingAddressHeading }}: {{ invoiceAddressText }}</p>
                    <SmsTopUpTotal v-if="state.invoiceTotal" :net="state.invoiceTotal.net" :total="state.invoiceTotal.total" :currencyCode="currency ?? ''" />
                    <FormInput
                        class="form-group"
                        :label="$Resources.SmsTopUpReferenceLabel"
                        :modelValue="reference"
                        :maxlength="MAX_REFERENCE_LENGTH"
                        :disabled="state.busy"
                        @inputElOnMounted="(el: HTMLInputElement | null) => (referenceInput = el)"
                        @update:modelValue="(newVal) => (reference = String(newVal ?? ''))"></FormInput>
                    <p id="sms-top-up-reference-message" class="c-light" role="status">{{ referenceInvalid ? $Resources.SmsTopUpInvalidReferenceMessage : '' }}</p>
                    <SmsTopUpInvoiceRecipient v-model="recipient" :disabled="state.busy" />

                    <div class="text-right">
                        <button class="btn btn-default m-sm-r" :disabled="state.busy" @click="closeInvoiceModal">{{ $Resources.CancelBtn }}</button>
                        <button class="btn btn-primary" :class="{ 'is-busy': state.busy }" :disabled="!canInvoice || !state.invoiceTotal" @click="addToInvoice">{{ $Resources.SmsTopUpInvoiceConfirmBtn }}</button>
                    </div>
                </template>
            </template>
        </Modal>
    </template>
</template>
